import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import {
  FiPlus,
  FiEdit2,
  FiMail,
  FiUser,
  FiX,
  FiCheck,
  FiLayers,
  FiTrash2,
} from "react-icons/fi";
import Loader from "../../components/Global/Loader";
import PasswordInput from "../../components/Global/PasswordInput";
import {
  fetchSalesPersons,
  registerSalesPerson,
  updateSalesPersonBoards,
  deleteSalesPerson,
} from "../../services/salesPersonApi";
import { fetchKanbanBoards } from "../../services/crmApi";

const passwordRegex =
  /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RegisterModal = ({ boards, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm_password: "",
    assignedBoardIds: [],
  });
  const [saving, setSaving] = useState(false);

  const toggleBoard = (id) => {
    setForm((f) => ({
      ...f,
      assignedBoardIds: f.assignedBoardIds.includes(id)
        ? f.assignedBoardIds.filter((b) => b !== id)
        : [...f.assignedBoardIds, id],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = form.name.trim();
    if (!trimmedName) return toast.error("Name is required");
    if (trimmedName.length < 2)
      return toast.error("Name must be at least 2 characters");
    if (!emailRegex.test(form.email.trim()))
      return toast.error("Please enter a valid email");
    if (!passwordRegex.test(form.password))
      return toast.error(
        "Password must be 8+ chars, include uppercase, number and special char."
      );
    if (form.password !== form.confirm_password)
      return toast.error("Passwords do not match");

    try {
      setSaving(true);
      const res = await registerSalesPerson({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        confirm_password: form.confirm_password,
        assignedBoardIds: form.assignedBoardIds,
      });
      toast.success(res?.message || "Sales person registered successfully");
      onSaved();
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col shadow-lg">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            Register Sales Person
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX size={22} />
          </button>
        </div>
        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-6 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
              placeholder="jane@example.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <PasswordInput
                autoComplete="new-password"
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
                inputClassName="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <PasswordInput
                autoComplete="new-password"
                value={form.confirm_password}
                onChange={(e) =>
                  setForm({ ...form, confirm_password: e.target.value })
                }
                inputClassName="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Must be 8+ chars, include uppercase, number and a special character.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign Boards
            </label>
            {boards.length === 0 ? (
              <p className="text-xs text-gray-400">No boards available</p>
            ) : (
              <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                {boards.map((b) => (
                  <label
                    key={b._id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={form.assignedBoardIds.includes(b._id)}
                      onChange={() => toggleBoard(b._id)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{b.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md disabled:opacity-50"
            >
              {saving ? "Registering..." : "Register"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditBoardsModal = ({ salesPerson, boards, onClose, onSaved }) => {
  const [assigned, setAssigned] = useState(
    Array.isArray(salesPerson.assignedBoardIds)
      ? salesPerson.assignedBoardIds.map((b) =>
          typeof b === "object" ? b._id || b.id : b
        )
      : []
  );
  const [saving, setSaving] = useState(false);

  const toggleBoard = (id) => {
    setAssigned((arr) =>
      arr.includes(id) ? arr.filter((b) => b !== id) : [...arr, id]
    );
  };

  const handleSave = async () => {
    const spId = salesPerson?._id || salesPerson?.id;
    if (!spId) {
      toast.error("Sales person id is missing. Please refresh and try again.");
      return;
    }
    try {
      setSaving(true);
      const res = await updateSalesPersonBoards(spId, assigned);
      toast.success(res?.message || "Boards updated successfully");
      onSaved();
    } catch (err) {
      // interceptor handles
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-lg">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Edit Assigned Boards
            </h2>
            <p className="text-xs text-gray-500 mt-1">{salesPerson.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX size={22} />
          </button>
        </div>
        <div className="p-6 space-y-3">
          {boards.length === 0 ? (
            <p className="text-sm text-gray-400">No boards available</p>
          ) : (
            <div className="border border-gray-200 rounded-md max-h-72 overflow-y-auto">
              {boards.map((b) => (
                <label
                  key={b._id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                >
                  <input
                    type="checkbox"
                    checked={assigned.includes(b._id)}
                    onChange={() => toggleBoard(b._id)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{b.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SalesPersons = () => {
  const [salesPersons, setSalesPersons] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [editingSp, setEditingSp] = useState(null);
  const [deletingSp, setDeletingSp] = useState(null);
  const [deletingInFlight, setDeletingInFlight] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [spRes, boardsRes] = await Promise.allSettled([
        fetchSalesPersons(),
        fetchKanbanBoards(),
      ]);
      if (spRes.status === "fulfilled") {
        const list = Array.isArray(spRes.value?.data)
          ? spRes.value.data
          : Array.isArray(spRes.value)
            ? spRes.value
            : [];
        // Normalize id field (backend may return `id` or `_id`)
        setSalesPersons(
          list.map((sp) => ({ ...sp, _id: sp._id || sp.id }))
        );
      } else {
        setSalesPersons([]);
      }
      if (boardsRes.status === "fulfilled") {
        const list = Array.isArray(boardsRes.value?.data)
          ? boardsRes.value.data
          : Array.isArray(boardsRes.value)
            ? boardsRes.value
            : [];
        setBoards(list.map((b) => ({ ...b, _id: b._id || b.id })));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDeleteSp = async () => {
    const spId = deletingSp?._id || deletingSp?.id;
    if (!spId) {
      toast.error("Sales person id is missing. Please refresh and try again.");
      return;
    }
    try {
      setDeletingInFlight(true);
      const res = await deleteSalesPerson(spId);
      // The backend revokes CRM access for consultant entries (profile kept)
      // and fully deletes real SalesPerson accounts. Pick the toast to match.
      const action = res?.data?.action;
      const fallback =
        action === "revoked"
          ? `CRM access revoked from "${deletingSp.name}"`
          : `Sales person "${deletingSp.name}" deleted`;
      toast.success(res?.message || fallback);
      setDeletingSp(null);
      await load();
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setDeletingInFlight(false);
    }
  };

  const getBoardNames = (sp) => {
    const ids = (sp.assignedBoardIds || []).map((b) =>
      typeof b === "object" ? b._id || b.id : b
    );
    return boards
      .filter((b) => ids.includes(b._id))
      .map((b) => b.name);
  };

  if (loading) return <Loader />;

  return (
    <div className="pl-36 mt-16 min-h-screen bg-[#f4f8fb] p-8">
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">
              Sales Persons
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage sales person accounts and board assignments
            </p>
          </div>
          <button
            onClick={() => setShowRegister(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md flex items-center gap-2"
          >
            <FiPlus size={18} />
            Register Sales Person
          </button>
        </div>
      </div>

      {salesPersons.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
            <FiUser size={28} className="text-blue-600" />
          </div>
          <p className="text-gray-700 text-lg font-medium mb-2">
            No sales persons yet
          </p>
          <p className="text-gray-500 text-sm mb-6">
            Register your first sales person to assign boards
          </p>
          <button
            onClick={() => setShowRegister(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md inline-flex items-center gap-2"
          >
            <FiPlus size={20} />
            Register Sales Person
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {salesPersons.map((sp) => {
            const boardNames = getBoardNames(sp);
            return (
              <div
                key={sp._id}
                className="bg-white rounded-lg shadow-md p-5 flex flex-col"
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-semibold">
                      {(sp.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-800 truncate flex items-center gap-1.5">
                        <span className="truncate">{sp.name}</span>
                        <span
                          className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            sp.type === "Consultant"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-blue-100 text-blue-800"
                          }`}
                        >
                          {sp.type === "Consultant" ? "Consultant" : "Sales"}
                        </span>
                      </h3>
                      <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                        <FiMail size={11} /> {sp.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setEditingSp(sp)}
                      className="p-2 hover:bg-gray-100 text-gray-600 rounded"
                      title="Edit assigned boards"
                    >
                      <FiEdit2 size={16} />
                    </button>
                    <button
                      onClick={() => setDeletingSp(sp)}
                      className="p-2 hover:bg-red-50 text-red-600 rounded"
                      title={
                        sp.type === "Consultant"
                          ? "Remove CRM access"
                          : "Delete sales person"
                      }
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-xs text-gray-600 mb-2 flex items-center gap-1">
                    <FiLayers size={12} /> Assigned Boards
                  </p>
                  {boardNames.length === 0 ? (
                    <p className="text-xs text-gray-400">No boards assigned</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {boardNames.map((n) => (
                        <span
                          key={n}
                          className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[11px]"
                        >
                          <FiCheck size={10} />
                          {n}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showRegister && (
        <RegisterModal
          boards={boards}
          onClose={() => setShowRegister(false)}
          onSaved={() => {
            setShowRegister(false);
            load();
          }}
        />
      )}

      {editingSp && (
        <EditBoardsModal
          salesPerson={editingSp}
          boards={boards}
          onClose={() => setEditingSp(null)}
          onSaved={() => {
            setEditingSp(null);
            load();
          }}
        />
      )}

      {deletingSp &&
        (() => {
          const isConsultantRow = deletingSp.type === "Consultant";
          return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  {isConsultantRow
                    ? "Remove CRM Access"
                    : "Delete Sales Person"}
                </h3>
                <p className="text-gray-600 mb-2">
                  {isConsultantRow ? (
                    <>
                      Remove CRM access from consultant{" "}
                      <span className="font-semibold">
                        "{deletingSp.name}"
                      </span>
                      ?
                    </>
                  ) : (
                    <>
                      Are you sure you want to delete{" "}
                      <span className="font-semibold">
                        "{deletingSp.name}"
                      </span>
                      ?
                    </>
                  )}
                </p>
                <p className="text-xs text-gray-500 mb-6">
                  {isConsultantRow
                    ? "This only switches off their sales person role and clears their board assignments. Their consultant profile stays intact, and leads they own remain assigned to them so you can reassign them later."
                    : "This will permanently remove the sales person account and revoke their access to assigned boards. Leads they own will remain on their boards but become unassigned. This action cannot be undone."}
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => setDeletingSp(null)}
                    disabled={deletingInFlight}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteSp}
                    disabled={deletingInFlight}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50"
                  >
                    {deletingInFlight
                      ? isConsultantRow
                        ? "Removing..."
                        : "Deleting..."
                      : isConsultantRow
                        ? "Remove Access"
                        : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default SalesPersons;
